import { useState } from "react";
import { getEditModeItemsQuantity, getEditModeLineItems, getLineItems, stopEditingMode } from "./utils";
import { getSelectedPurchasableMainProductVariant, getSingleProductVariantFromMainProductByLengthInTitle, isEditMode } from "../../../utils";
import { useApp } from "../../../hooks/useApp";

interface Props {
  className?: string;
  wrapperClassName?: string;
}

export const AddToCartButton = ({ className, wrapperClassName }: Props) => {
  const { mainProduct, activeMainProductVariantId, activePendantProductVariantIds, cart, isMainProductSinglePendant} = useApp()
  const selectedPurchasableVariant = getSelectedPurchasableMainProductVariant(
    mainProduct,
    activeMainProductVariantId,
    activePendantProductVariantIds,
    isMainProductSinglePendant
  )
  const isSoldOut = !selectedPurchasableVariant?.available
  const showNotifyMe = isSoldOut && mainProduct.globalSettings.klaviyoBisEnabled
  const notifyMeText = mainProduct.globalSettings.notifyMeText || 'Notify Me When Available'
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">(
      "idle"
    );

  

  const handleAddToCart = async () => {
    if (isSoldOut) return

    // 3 cases:
    // 1. Multiple pendants mainProduct:
    // 1.1. More than 1 pendant variant -> add chain product variant and pendant variants
    // 1.2. Only 1 pendant variant -> add linked single product variant
    // 2. Single pendant mainProduct 
    // 2.1. Add main product variant depending on selected charm and length

    setStatus("loading");

    const quantity = isEditMode() ? getEditModeItemsQuantity() : 1
    
    const url = new URL(window.location.href);
    url.searchParams.delete("edit_qts");
    url.searchParams.delete("edit_mode");
    url.searchParams.delete("edit_q");

    const bundleUrl = url.toString();

    let itemsToAdd = getLineItems(mainProduct, activeMainProductVariantId, activePendantProductVariantIds, quantity, bundleUrl, isMainProductSinglePendant)
    const itemsToRemove = getEditModeLineItems(mainProduct, cart)

    // @TODO Extract to the function above
    if (activePendantProductVariantIds.length === 1 && !isMainProductSinglePendant) {
      const linkedSingleProductVariant = getSingleProductVariantFromMainProductByLengthInTitle(
        mainProduct, 
        Number(activeMainProductVariantId),
        Number(activePendantProductVariantIds[0])
      )
      
      if (linkedSingleProductVariant?.available) {
        itemsToAdd = [
          {
            id: String(linkedSingleProductVariant.id),
            quantity: 1,
            properties: {
              _url: bundleUrl
            }
          }
        ]
      }
    }
  
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    }

    const addItems = () => fetch("/cart/add.js", { 
      ...requestOptions,
      body: JSON.stringify({ items: itemsToAdd }),
    })

    const removeItems = () => fetch("/cart/update.js", { 
      ...requestOptions,
      body: JSON.stringify({ updates: itemsToRemove }),
    })

    Promise.all([
      addItems(), 
      itemsToRemove && removeItems()
    ])
      .then(res => {
        const [addItemsRes, removeItemsRes] = res;
        if (!addItemsRes.ok || removeItemsRes !== null && !removeItemsRes.ok) throw new Error("Failed to update cart")
      })
      .then(() => {
        setStatus("success");

        document.dispatchEvent(
          new CustomEvent('product:added', {
            bubbles: true,
            detail: {
              variant: {
                name: mainProduct.title
              },
              quantity
            }
          })
        )

        isEditMode() && stopEditingMode()

        setTimeout(() => setStatus("idle"), 2000);
      })
      .catch((err) => {
         console.error(err);
        setStatus("error");
        setTimeout(() => setStatus("idle"), 2000);
      })
  };

  return (
    <>
      <button
        type="button"
        className={`ProductForm__AddToCart ProductForm__AddToCart_Desktop Button Button--tertiary ${className ?? ''}`}
        disabled={status === "loading" || isSoldOut}
        onClick={handleAddToCart}
      >
        <div className="AddToCartPriceContentWrapper AddToCartPriceContentWrapper__NoPrice">
          <span className="ProductForm_label-atc">
            {isSoldOut
              ? "Sold Out"
              : status === "loading"
              ? "•••"
              : status === "error"
              ? "Error"
              : isEditMode()
              ? "Edit"
              : "Add to Bag"
            }
          </span>
        </div>
      </button>

      {showNotifyMe && (
        <button
          className="klaviyo-bis-trigger klaviyo-product-trigger Button Button--tertiary Button--full mt-2"
          data-action="notify-me"
          data-variant={String(selectedPurchasableVariant?.id ?? activeMainProductVariantId)}
          type="button"
        >
          <div className="AddToCartPriceContentWrapper AddToCartPriceContentWrapper__NoPrice">
            <span className="ProductForm_label-atc">{notifyMeText}</span>
          </div>
        </button>
      )}
    </>
  )
}
